import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useTenant } from "../contexts/tenant-context";
import { useSnackbar } from "../contexts/snackbar-context";
import { useTenantManagementService } from "../services/tenantManagementService";
import { useShopVisitReasonService } from "../api/shopVisitReasonService";
import { ShopVisitReasonComponentType, ShopVisitReasonItem } from "../types/shopVisitReason";
import { ConfirmDialog } from "../components/common/confirm-dialog";
import { FileUploadArea } from "../components/documents/upload/file-upload-area";
import { MiniPackTemplateDto, MiniPackTemplateType } from "../services/tenantManagementService";
import JsonTemplatePreview from "../components/common/json-template-preview";
import { OperatorsTab } from "../components/tenant-management/operators-tab";
import { DisplayPreferencesTab } from "../components/tenant-management/display-preferences-tab";
import { tenantManagementTabsSx } from "../components/tenant-management/tenantManagementStyles";
import { useHasPermission } from "../hooks/useHasPermission";
const TAB_TENANT_DETAILS = 0;
const TAB_OFFWING_REASONS = 1;
const TAB_MINIPACK_TEMPLATES = 2;
const TAB_OPERATORS = 3;
const TAB_DISPLAY_PREFERENCES = 4;

type EditorState = {
  open: boolean;
  isEdit: boolean;
  id?: string;
  componentType: ShopVisitReasonComponentType;
  label: string;
};

const defaultEditorState: EditorState = {
  open: false,
  isEdit: false,
  componentType: "Engine",
  label: "",
};

type TenantFormState = {
  name: string;
  description: string;
  status: "Active" | "Inactive";
  logoFile: File | null;
  logoPreview: string | null;
};

const miniPackTemplateOptions: Array<{ value: MiniPackTemplateType; label: string }> = [
  { value: "Airframe", label: "Airframe" },
  { value: "Engine", label: "Engine" },
  { value: "APU", label: "APU" },
  { value: "SubAssembly", label: "Landing Gear Sub-Assembly" },
  { value: "LandingGear", label: "Landing Gear" },
];

const formatMiniPackTemplateType = (value: MiniPackTemplateType): string => {
  if (value === "SubAssembly") return "Landing Gear Sub-Assembly";
  return value === "LandingGear" ? "Landing Gear" : value;
};

const defaultTenantForm: TenantFormState = {
  name: "",
  description: "",
  status: "Active",
  logoFile: null,
  logoPreview: null,
};

const componentTypeLabel = (value: ShopVisitReasonComponentType): string => {
  if (value === "LandingGear") return "Landing Gear";
  return value;
};

const TenantManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { selectedTenantId, selectedTenant, setUserSession, userSession } = useTenant();
  const { showSuccess, showError } = useSnackbar();
  const {
    getTenants,
    getTenantFeatureKeys,
    updateTenant,
    getMiniPackTemplates,
    uploadMiniPackTemplate,
    getMiniPackTemplateContent,
  } = useTenantManagementService();
  const { getReasons, createReason, updateReason, deleteReason } = useShopVisitReasonService();

  const [activeTab, setActiveTab] = useState(0);

  const [tenantForm, setTenantForm] = useState<TenantFormState>(defaultTenantForm);
  const [tenantDetailsLoading, setTenantDetailsLoading] = useState(true);
  const [tenantDetailsSaving, setTenantDetailsSaving] = useState(false);

  const [reasonsLoading, setReasonsLoading] = useState(true);
  const [reasonsSaving, setReasonsSaving] = useState(false);
  const [reasons, setReasons] = useState<ShopVisitReasonItem[]>([]);
  const [editor, setEditor] = useState<EditorState>(defaultEditorState);
  const [deleteTarget, setDeleteTarget] = useState<ShopVisitReasonItem | null>(null);
  const [deleteWarning, setDeleteWarning] = useState<string | null>(null);
  const [miniPackTemplatesLoading, setMiniPackTemplatesLoading] = useState(true);
  const [miniPackTemplatesUploading, setMiniPackTemplatesUploading] = useState(false);
  const [miniPackTemplates, setMiniPackTemplates] = useState<MiniPackTemplateDto[]>([]);
  const [miniPackTemplateType, setMiniPackTemplateType] = useState<MiniPackTemplateType>("Airframe");
  const [miniPackTemplateFile, setMiniPackTemplateFile] = useState<File | null>(null);
  const [miniPackDragActive, setMiniPackDragActive] = useState(false);
  const [miniPackPreviewContent, setMiniPackPreviewContent] = useState<string | null>(null);
  const [miniPackPreviewTitle, setMiniPackPreviewTitle] = useState<string>("Template Preview");
  const [miniPackPreviewLoading, setMiniPackPreviewLoading] = useState(false);
  const [selectedPreviewType, setSelectedPreviewType] = useState<MiniPackTemplateType | null>(null);

  const isGlobalAdminScope = !selectedTenantId || selectedTenantId === "__GLOBAL_ADMIN__";
  const isTenantAdmin = selectedTenant?.role?.toLowerCase() === "admin";
  const canManageTenant = !isGlobalAdminScope && isTenantAdmin;
  const canAccessOperatorFeatures = useHasPermission("assets-operator", "read");

  const loadTenantDetails = useCallback(async () => {
    if (!selectedTenantId || selectedTenantId === "__GLOBAL_ADMIN__") {
      setTenantForm(defaultTenantForm);
      setTenantDetailsLoading(false);
      return;
    }

    setTenantDetailsLoading(true);
    try {
      const tenants = await getTenants();
      const tenant = tenants.find((x) => x.id === selectedTenantId);

      setTenantForm({
        name: tenant?.name ?? selectedTenant?.tenantName ?? "",
        description: ((tenant as any)?.description ?? "") as string,
        status: ((tenant?.status as "Active" | "Inactive") ?? "Active"),
        logoFile: null,
        logoPreview: tenant?.logoUrl ?? selectedTenant?.logoUrl ?? null,
      });
    } finally {
      setTenantDetailsLoading(false);
    }
  }, [getTenants, selectedTenantId, selectedTenant]);

  const loadReasons = useCallback(async () => {
    if (!canManageTenant) {
      setReasons([]);
      setReasonsLoading(false);
      return;
    }

    setReasonsLoading(true);
    try {
      const items = await getReasons();
      setReasons(items);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load offwing reasons.";
      showError(message);
    } finally {
      setReasonsLoading(false);
    }
  }, [canManageTenant, getReasons, showError]);

  const loadMiniPackTemplates = useCallback(async () => {
    if (!canManageTenant) {
      setMiniPackTemplates([]);
      setMiniPackTemplatesLoading(false);
      return;
    }

    setMiniPackTemplatesLoading(true);
    try {
      const items = await getMiniPackTemplates();
      setMiniPackTemplates(items);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load mini-pack templates.";
      showError(message);
    } finally {
      setMiniPackTemplatesLoading(false);
    }
  }, [canManageTenant, getMiniPackTemplates, showError]);

  useEffect(() => {
    void loadTenantDetails();
  }, [loadTenantDetails]);

  useEffect(() => {
    void loadReasons();
  }, [loadReasons]);

  useEffect(() => {
    void loadMiniPackTemplates();
  }, [loadMiniPackTemplates]);

  useEffect(() => {
    if (!canAccessOperatorFeatures && (activeTab === TAB_OPERATORS || activeTab === TAB_DISPLAY_PREFERENCES)) {
      setActiveTab(TAB_TENANT_DETAILS);
    }
  }, [canAccessOperatorFeatures, activeTab]);

  const handleTenantLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setTenantForm((prev) => ({
      ...prev,
      logoFile: file,
      logoPreview: objectUrl,
    }));
  };

  const handleCancelLogoChange = () => {
    void loadTenantDetails();
  };

  const handleSaveTenantDetails = async () => {
    if (!selectedTenantId || selectedTenantId === "__GLOBAL_ADMIN__") {
      return;
    }

    const name = tenantForm.name.trim();

    setTenantDetailsSaving(true);

    try {
      const featureKeys = await getTenantFeatureKeys(selectedTenantId);
      await updateTenant(selectedTenantId, {
        name,
        description: tenantForm.description.trim(),
        status: tenantForm.status,
        featureKeys,
        logoFile: tenantForm.logoFile,
      });

      if (userSession && setUserSession) {
        const updatedTenants = (userSession.tenants ?? []).map((tenant) => {
          if (tenant.tenantId !== selectedTenantId) {
            return tenant;
          }

          return {
            ...tenant,
            tenantName: name,
            logoUrl: tenantForm.logoPreview || tenant.logoUrl,
          };
        });

        setUserSession({
          ...userSession,
          tenants: updatedTenants,
        });
      }

      showSuccess("Logo updated successfully");
      await loadTenantDetails();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update logo.";
      showError(message);
    } finally {
      setTenantDetailsSaving(false);
    }
  };

  const handleOpenAddReason = () => {
    setEditor({ ...defaultEditorState, open: true });
  };

  const handleOpenEditReason = (item: ShopVisitReasonItem) => {
    setEditor({
      open: true,
      isEdit: true,
      id: item.id,
      componentType: item.componentType,
      label: item.label,
    });
  };

  const handleCloseReasonEditor = () => {
    if (reasonsSaving) return;
    setEditor(defaultEditorState);
  };

  const handleSaveReason = async () => {
    const label = editor.label.trim();
    if (!label) {
      showError("Reason text is required.");
      return;
    }

    setReasonsSaving(true);

    try {
      if (editor.isEdit && editor.id) {
        await updateReason(editor.id, {
          componentType: editor.componentType,
          label,
        });
      } else {
        await createReason({
          componentType: editor.componentType,
          label,
        });
      }

      setEditor(defaultEditorState);
      showSuccess(editor.isEdit ? "Offwing reason updated" : "Offwing reason added");
      await loadReasons();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save reason.";
      showError(message);
    } finally {
      setReasonsSaving(false);
    }
  };

  const handleConfirmDeleteReason = async () => {
    if (!deleteTarget) return;

    setReasonsSaving(true);

    try {
      const force = !!deleteWarning;
      await deleteReason(deleteTarget.id, force);
      setDeleteTarget(null);
      setDeleteWarning(null);
      showSuccess("Offwing reason deleted");
      await loadReasons();
    } catch (err: any) {
      if (err?.status === 409 && err?.details?.requiresConfirmation) {
        setDeleteWarning(err.details.warningMessage);
      } else {
        const message = err instanceof Error ? err.message : "Failed to delete reason.";
        showError(message);
      }
    } finally {
      setReasonsSaving(false);
    }
  };

  const reasonsByType = useMemo(() => {
    const grouped: Record<string, ShopVisitReasonItem[]> = {};
    reasons.forEach((item) => {
      const type = item.componentType;
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(item);
    });

    // Sort each group by label and return in order (Engine, APU, LandingGear)
    const order = ["Engine", "APU", "LandingGear"];
    const result: Record<string, ShopVisitReasonItem[]> = {};
    order.forEach((type) => {
      if (grouped[type]) {
        result[type] = grouped[type].sort((a, b) => a.label.localeCompare(b.label));
      }
    });

    return result;
  }, [reasons]);

  const miniPackTemplatesByType = useMemo(() => {
    const map = new Map<MiniPackTemplateType, MiniPackTemplateDto>();
    miniPackTemplates.forEach((item) => {
      map.set(item.templateType, item);
    });

    return map;
  }, [miniPackTemplates]);

  const readJsonTemplateContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Failed to read JSON file."));
      reader.readAsText(file);
    });
  };

  const handleMiniPackFileSelect = async (files: FileList) => {
    const file = files[0];
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".json")) {
      showError("Only JSON files are allowed.");
      return;
    }

    setMiniPackTemplateFile(file);

    setMiniPackPreviewLoading(true);
    try {
      const content = await readJsonTemplateContent(file);
      setMiniPackPreviewContent(content);
      setMiniPackPreviewTitle(`${formatMiniPackTemplateType(miniPackTemplateType)} Preview (${file.name})`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to read JSON template.";
      showError(message);
      setMiniPackPreviewContent(null);
      setMiniPackPreviewTitle("Template Preview");
    } finally {
      setMiniPackPreviewLoading(false);
    }
  };

  const handleMiniPackDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setMiniPackDragActive(false);

    if (event.dataTransfer.files?.length) {
      handleMiniPackFileSelect(event.dataTransfer.files);
    }
  };

  const handleMiniPackUpload = async () => {
    if (!miniPackTemplateFile) {
      showError("Select a JSON template to upload.");
      return;
    }

    setMiniPackTemplatesUploading(true);
    try {
      await uploadMiniPackTemplate(miniPackTemplateType, miniPackTemplateFile);
      showSuccess(`${formatMiniPackTemplateType(miniPackTemplateType)} mini-pack template uploaded.`);
      setMiniPackTemplateFile(null);
      await loadMiniPackTemplates();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to upload mini-pack template.";
      showError(message);
    } finally {
      setMiniPackTemplatesUploading(false);
    }
  };

  const handleTemplateRowClick = async (template: MiniPackTemplateDto | undefined, optionType: MiniPackTemplateType) => {
    if (!template) {
      setSelectedPreviewType(optionType);
      setMiniPackPreviewTitle(`${formatMiniPackTemplateType(optionType)} Preview`);
      setMiniPackPreviewContent(null);
      return;
    }

    setSelectedPreviewType(optionType);
    setMiniPackPreviewLoading(true);
    try {
      const content = await getMiniPackTemplateContent(template.id);
      setMiniPackPreviewContent(content);
      setMiniPackPreviewTitle(`${formatMiniPackTemplateType(optionType)} Preview (${template.fileName})`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load template preview.";
      showError(message);
    } finally {
      setMiniPackPreviewLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, mb: 2 }}>
        Tenant Management
      </Typography>

      {isGlobalAdminScope && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a tenant to open Tenant Management.
        </Alert>
      )}

      {!isGlobalAdminScope && !isTenantAdmin && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only tenant Admin can access tenant management.
        </Alert>
      )}

      {!canManageTenant ? null : (
        <>
          <Tabs
            value={activeTab}
            onChange={(_, next) => setActiveTab(next)}
            sx={tenantManagementTabsSx}
          >
            <Tab label="Tenant Details" />
            <Tab label="Offwing Reasons" />
            <Tab label="Mini-Pack Templates" />
            {canAccessOperatorFeatures && <Tab label="Operators" />}
            {canAccessOperatorFeatures && <Tab label="Display Preferences" />}
          </Tabs>

          {activeTab === TAB_TENANT_DETAILS && (
            <Paper sx={{ bgcolor: "#111", color: "#fff", border: "1px solid #333", p: 2 }}>
              {tenantDetailsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Avatar
                    variant="square"
                    sx={{
                      width: 90,
                      height: 50,
                      bgcolor: "hsl(19 101% 51% / 0.25)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0.5,
                    }}
                  >
                    {tenantForm.logoPreview ? (
                      <Box
                        component="img"
                        src={tenantForm.logoPreview}
                        alt={tenantForm.name}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      tenantForm.name?.charAt(0) || "T"
                    )}
                  </Avatar>

                  <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600, flex: 1 }}>
                    {tenantForm.name || selectedTenant?.tenantName}
                  </Typography>

                  {tenantDetailsSaving ? (
                    <CircularProgress size={24} sx={{ color: "hsl(19 101% 51% / 1)" }} />
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {!tenantForm.logoFile && (
                        <Button
                          variant="contained"
                          component="label"
                          disabled={tenantDetailsSaving}
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            backgroundColor: "hsl(19 101% 51% / 1)",
                            color: "#fff",
                            '&:hover': { backgroundColor: "hsl(19 101% 41% / 1)" },
                            '&:disabled': {
                              backgroundColor: "hsl(19 101% 51% / 0.4)",
                              color: "#fff",
                            },
                          }}
                        >
                          Upload Logo
                          <input hidden type="file" accept="image/*" onChange={handleTenantLogoChange} />
                        </Button>
                      )}

                      {tenantForm.logoFile && (
                        <>
                          <IconButton
                            size="small"
                            onClick={handleCancelLogoChange}
                            sx={{ color: "#f44336", border: "1px solid #333" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleSaveTenantDetails}
                            sx={{
                              backgroundColor: "hsl(19 101% 51% / 1)",
                              color: "#fff",
                              '&:hover': { backgroundColor: "hsl(19 101% 41% / 1)" },
                            }}
                          >
                            Save
                          </Button>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          )}

          {activeTab === TAB_OFFWING_REASONS && (
            <Paper sx={{ bgcolor: "#111", color: "#fff", border: "1px solid #333", p: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 2 }} spacing={2}>
                <Typography variant="h6" sx={{ color: "hsl(19 101% 51% / 1)" }}>
                  Offwing Reasons
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddReason}
                  sx={{
                    bgcolor: "hsl(19 101% 51% / 1)",
                    "&:hover": { bgcolor: "hsl(19 101% 41% / 1)" },
                  }}
                >
                  Add Reason
                </Button>
              </Stack>

              {reasonsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : Object.keys(reasonsByType).length === 0 ? (
                <Typography sx={{ color: "#999", textAlign: "center", py: 4 }}>
                  No reasons configured.
                </Typography>
              ) : (
                <Stack spacing={0}>
                  {["Engine", "APU", "LandingGear"].map((type) => {
                    const items = reasonsByType[type] || [];
                    if (items.length === 0) return null;

                    return (
                      <Accordion
                        key={type}
                        defaultExpanded
                        sx={{
                          bgcolor: "#111",
                          color: "#fff",
                          border: "1px solid #333",
                          mb: 0.5,
                          "&:before": { display: "none" },
                          "&.Mui-expanded": { m: 0, mb: 0.5 },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: "hsl(19 101% 51% / 1)" }} />}
                          sx={{
                            bgcolor: "#1a1a1a",
                            borderBottom: "1px solid #333",
                            "&:hover": { bgcolor: "#222" },
                          }}
                        >
                          <Typography sx={{ color: "hsl(19 101% 51% / 1)", fontWeight: 600, flex: 1 }}>
                            {componentTypeLabel(type as ShopVisitReasonComponentType)}
                          </Typography>
                          <Typography sx={{ color: "#999", mr: 2 }}>
                            ({items.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ color: "#fff", borderColor: "#333", background: "#0a0a0a" }}>
                                    Reason
                                  </TableCell>
                                  <TableCell sx={{ color: "#fff", borderColor: "#333", width: 100, background: "#0a0a0a" }}>
                                    Actions
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {items.map((item, index) => (
                                  <TableRow key={`${type}-${String(item.id)}-${item.label}-${index}`} sx={{ "&:hover": { bgcolor: "#1a1a1a" } }}>
                                    <TableCell sx={{ color: "#fff", borderColor: "#333" }}>
                                      {item.label}
                                    </TableCell>
                                    <TableCell sx={{ borderColor: "#333" }}>
                                      <IconButton
                                        size="small"
                                        sx={{ color: "hsl(19 101% 51% / 1)", mr: 0.5 }}
                                        onClick={() => handleOpenEditReason(item)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        sx={{ color: "#f44336" }}
                                        onClick={() => setDeleteTarget(item)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          )}

          {activeTab === TAB_MINIPACK_TEMPLATES && (
            <Paper sx={{ bgcolor: "#111", color: "#fff", border: "1px solid #333", p: { xs: 1.5, sm: 2 } }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ color: "hsl(19 101% 51% / 1)", mb: 1 }}>
                    Mini-Pack Templates
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#bdbdbd" }}>
                    Select the template type above, then upload a JSON file with a sections array.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      lg: "minmax(300px, 360px) minmax(300px, 1fr) minmax(300px, 1.1fr)",
                    },
                    gap: { xs: 1.5, sm: 2 },
                    alignItems: "start",
                  }}
                >
                  <Paper sx={{ bgcolor: "#151515", border: "1px solid #333", p: { xs: 1, sm: 1.5 } }}>
                    <Stack spacing={2}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: "#fff", "&.Mui-focused": { color: "#fff" }, "&.MuiInputLabel-shrink": { color: "#fff" } }}>
                          Template Type
                        </InputLabel>
                        <Select
                          label="Template Type"
                          value={miniPackTemplateType}
                          onChange={(e) => setMiniPackTemplateType(e.target.value as MiniPackTemplateType)}
                          sx={{
                            color: "#fff",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                            "& .MuiSvgIcon-root": { color: "#fff" },
                          }}
                        >
                          {miniPackTemplateOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FileUploadArea
                        isDragging={miniPackDragActive}
                        onDrop={handleMiniPackDrop}
                        onDragOver={(event) => {
                          event.preventDefault();
                          setMiniPackDragActive(true);
                        }}
                        onDragLeave={(event) => {
                          event.preventDefault();
                          setMiniPackDragActive(false);
                        }}
                        onFileSelect={handleMiniPackFileSelect}
                        accept=".json,application/json,text/json"
                        multiple={false}
                        compact={true}
                        disabled={miniPackTemplatesUploading}
                        primaryText="Upload a JSON mini-pack template for the selected type."
                        dragText="Drag & drop a JSON file here"
                        dropText="Drop the JSON file here"
                        browseText="or click to browse"
                        maxInfoLabel="1 JSON file"
                      />

                      {miniPackTemplateFile && (
                        <Alert severity="info">
                          Selected file: {miniPackTemplateFile.name}
                        </Alert>
                      )}

                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleMiniPackUpload}
                        disabled={!miniPackTemplateFile || miniPackTemplatesUploading}
                        fullWidth={isMobile}
                        sx={{
                          alignSelf: { xs: "stretch", sm: "flex-start" },
                          bgcolor: "hsl(19 101% 51% / 1)",
                          "&:hover": { bgcolor: "hsl(19 101% 41% / 1)" },
                          "&:disabled": { bgcolor: "hsl(19 101% 51% / 0.4)", color: "#fff" },
                        }}
                      >
                        {miniPackTemplatesUploading ? "Uploading..." : "Upload Template"}
                      </Button>
                    </Stack>
                  </Paper>

                  <JsonTemplatePreview
                    title={miniPackPreviewTitle}
                    content={miniPackPreviewContent}
                    loading={miniPackPreviewLoading}
                    emptyMessage="Select a JSON template file to preview the structure before uploading."
                    minHeight={380}
                    paperSx={{
                      backgroundColor: "#151515",
                      border: "1px solid #333",
                    }}
                  />

                  <Paper sx={{ bgcolor: "#151515", border: "1px solid #333", p: { xs: 1, sm: 1.5 } }}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600 }}>
                        Added Templates
                      </Typography>

                      {miniPackTemplatesLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : miniPackTemplates.length === 0 ? (
                        <Typography sx={{ color: "#999", py: 3 }}>
                          No mini-pack templates uploaded yet.
                        </Typography>
                      ) : (
                        <TableContainer sx={{ maxHeight: { xs: 360, lg: 440 }, overflowY: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ color: "#fff", borderColor: "#333", background: "#0a0a0a" }}>
                                  Type
                                </TableCell>
                                <TableCell sx={{ color: "#fff", borderColor: "#333", background: "#0a0a0a" }}>
                                  File Name
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {miniPackTemplateOptions.map((option) => {
                                const template = miniPackTemplatesByType.get(option.value);

                                return (
                                <TableRow
                                  key={option.value}
                                  hover
                                  onClick={() => {
                                    void handleTemplateRowClick(template, option.value);
                                  }}
                                  sx={{
                                    cursor: "pointer",
                                    backgroundColor: selectedPreviewType === option.value ? "rgba(255,255,255,0.06)" : "transparent",
                                    "&:hover": { bgcolor: "#1a1a1a" },
                                  }}
                                >
                                  <TableCell sx={{ color: "#fff", borderColor: "#333" }}>
                                    {option.label}
                                  </TableCell>
                                  <TableCell sx={{ color: "#fff", borderColor: "#333" }}>
                                    {template?.fileName || "-"}
                                  </TableCell>
                                </TableRow>
                              )})}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Stack>
                  </Paper>
                </Box>
              </Stack>
            </Paper>
          )}

          {canAccessOperatorFeatures && activeTab === TAB_OPERATORS && (
            <OperatorsTab active={activeTab === TAB_OPERATORS} />
          )}

          {canAccessOperatorFeatures && activeTab === TAB_DISPLAY_PREFERENCES && (
            <DisplayPreferencesTab active={activeTab === TAB_DISPLAY_PREFERENCES} />
          )}
        </>
      )}

      <Dialog
        open={editor.open}
        onClose={handleCloseReasonEditor}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { backgroundColor: "#111", color: "#fff", border: "1px solid #333" } }}
      >
        <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
          {editor.isEdit ? "Edit Offwing Reason" : "Add Offwing Reason"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "#fff", "&.Mui-focused": { color: "#fff" }, "&.MuiInputLabel-shrink": { color: "#fff" } }}>
                Assembly Type
              </InputLabel>
              <Select
                label="Assembly Type"
                value={editor.componentType}
                onChange={(e) => setEditor((prev) => ({ ...prev, componentType: e.target.value as ShopVisitReasonComponentType }))}
                sx={{
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                  "& .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                <MenuItem value="Engine">Engine</MenuItem>
                <MenuItem value="APU">APU</MenuItem>
                <MenuItem value="LandingGear">Landing Gear</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Reason"
              fullWidth
              value={editor.label}
              onChange={(e) => setEditor((prev) => ({ ...prev, label: e.target.value }))}
              inputProps={{ maxLength: 200 }}
              sx={{
                "& .MuiInputLabel-root": { color: "#fff" },
                "& .MuiInputBase-input": { color: "#fff" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReasonEditor} sx={{ color: "#fff" }} disabled={reasonsSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveReason}
            variant="contained"
            disabled={reasonsSaving}
            sx={{ bgcolor: "hsl(19 101% 51% / 1)", "&:hover": { bgcolor: "hsl(19 101% 41% / 1)" } }}
          >
            {reasonsSaving ? "Saving..." : editor.isEdit ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Offwing Reason"
        message={
          deleteWarning
            ? deleteWarning
            : deleteTarget
              ? `Delete reason "${deleteTarget.label}"?`
              : ""
        }
        confirmLabel={deleteWarning ? "Yes, Delete" : "Delete"}
        cancelLabel="Cancel"
        isDangerous={true}
        onConfirm={handleConfirmDeleteReason}
        onCancel={() => { setDeleteTarget(null); setDeleteWarning(null); }}
        isLoading={reasonsSaving}
      />
    </Box>
  );
};

export default TenantManagementPage;
